import { useMemo } from "react";

import { UseManageLeverageQuotesParams, UseManageLeverageQuotesReturn, ManageLeverageAction } from "./types";
import { useIncreaseLeverageQuote } from "./useIncreaseLeverageQuote";
import { useRepayWithCollateralQuote } from "./useRepayWithCollateralQuote";

export function useManageLeverageQuotes({
    action,
    loanExpanded,
    loopPosition,
    slippagePercentDecimals,
    increasePrincipalAmount,
    repayWithCollateralAmount
}: UseManageLeverageQuotesParams): UseManageLeverageQuotesReturn {
    // 1. Increase leverage quote - only fetches when action is IncreaseLeverage
    const {
        increaseLeverageQuote,
        increaseLeverageQuoteLoading,
        increaseLeverageQuoteFetching,
        increaseLeverageQuoteError,
        validHealthIncreaseLeverageQuote
    } = useIncreaseLeverageQuote({
        loopPosition,
        loanExpanded,
        increasePrincipalAmount,
        slippagePercentDecimals,
        skip: action !== ManageLeverageAction.IncreaseLeverage
    });

    // 2. Decrease leverage quote - only fetches when action is DecreaseLeverage
    const {
        repayWithCollateralQuote,
        repayWithCollateralQuoteLoading,
        repayWithCollateralQuoteFetching,
        repayWithCollateralQuoteError,
        validHealthRepayQuote
    } = useRepayWithCollateralQuote({
        loanExpanded,
        repayWithCollateralAmount,
        slippagePercentDecimals,
        skip: action !== ManageLeverageAction.DecreaseLeverage
    });

    // Map action to quote data - only active action has defined quote
    const quoteMap = useMemo(
        () => ({
            [ManageLeverageAction.IncreaseLeverage]: {
                quote: action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageQuote : undefined,
                loading: action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageQuoteLoading : false,
                fetching: action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageQuoteFetching : false,
                error: action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageQuoteError : undefined,
                validHealth: action === ManageLeverageAction.IncreaseLeverage ? validHealthIncreaseLeverageQuote : true
            },
            [ManageLeverageAction.DecreaseLeverage]: {
                quote: action === ManageLeverageAction.DecreaseLeverage ? repayWithCollateralQuote : undefined,
                loading: action === ManageLeverageAction.DecreaseLeverage ? repayWithCollateralQuoteLoading : false,
                fetching: action === ManageLeverageAction.DecreaseLeverage ? repayWithCollateralQuoteFetching : false,
                error: action === ManageLeverageAction.DecreaseLeverage ? repayWithCollateralQuoteError : undefined,
                validHealth: action === ManageLeverageAction.DecreaseLeverage ? validHealthRepayQuote : true
            },
            [ManageLeverageAction.NoAction]: {
                quote: undefined,
                loading: false,
                fetching: false,
                error: undefined,
                validHealth: true
            }
        }),
        [
            action,
            increaseLeverageQuote,
            increaseLeverageQuoteLoading,
            increaseLeverageQuoteFetching,
            increaseLeverageQuoteError,
            validHealthIncreaseLeverageQuote,
            repayWithCollateralQuote,
            repayWithCollateralQuoteLoading,
            repayWithCollateralQuoteFetching,
            repayWithCollateralQuoteError,
            validHealthRepayQuote
        ]
    );

    return useMemo(() => {
        const actionQuote = quoteMap[action];

        return {
            quote: actionQuote?.quote,
            isLoading: actionQuote?.loading ?? false,
            isFetching: actionQuote?.fetching ?? false,
            error: actionQuote?.error,
            validHealth: actionQuote?.validHealth ?? true,
            action
        };
    }, [quoteMap, action]);
}
